import React from 'react';
import { formatCurrency } from '../../utils/formatters';
import { currencies, CurrencyCode } from '../../constants/currencies';

// 금액 분배 유형 정의
export type SplitType = 'equally' | 'parts' | 'amounts';

interface ParticipantListProps {
  selectedCurrency: CurrencyCode;
  members: any[];
  participants: Set<string>;
  currentUser: any;
  splitType: SplitType;
  amount: number;
  multipliers: Record<string, number>;
  manualAmounts: Record<string, number>;
  formattedManualAmounts: Record<string, string>;
  calculateShare: (memberId: string) => number;
  toggleParticipant: (memberId: string) => void;
  adjustMultiplier: (memberId: string, change: number) => void;
  handleManualAmountInputChange: (memberId: string, value: string) => void;
  handleManualAmountBlur: (memberId: string) => void;
  onSplitTypeChange: (splitType: SplitType) => void;
}

const ParticipantList: React.FC<ParticipantListProps> = ({
  selectedCurrency,
  members,
  participants,
  currentUser,
  splitType,
  amount,
  multipliers,
  manualAmounts,
  formattedManualAmounts,
  calculateShare,
  toggleParticipant,
  adjustMultiplier,
  handleManualAmountInputChange,
  handleManualAmountBlur,
  onSplitTypeChange,
}) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <label className="block" style={{fontSize: '17px', marginBottom: 0}}>
          참가한 사람
          <span className='text-gray-500' style={{fontSize: '15px'}}> ({participants.size}명/{members?.length}명)</span>
        </label>
        
        {/* 분배 방식 드롭다운 */}
        <div>
          <select
            className="bg-gray-50 text-gray-800 font-bold rounded-lg"
            style={{padding: '0.375em 1.75em 0.375em 0.625em', backgroundPosition: 'right 0.625em top 50%'}}
            value={splitType}
            onChange={(e) => onSplitTypeChange(e.target.value as SplitType)}
          >
            <option value="equally">N분의1</option>
            <option value="parts">배수입력</option>
            <option value="amounts">직접입력</option>
          </select>
        </div>
      </div>
      
      {/* 참가자 세로 리스트 */}
      <div className="border border-gray-100 rounded-lg divide-y divide-gray-100">
        {members?.map((member) => {
          const isSelected = participants.has(member.id);
          return (
            <div key={member.id} className="flex items-center px-4 py-3.5">
              {/* 체크박스 */}
              {splitType === 'equally' && (
                <div className="flex-shrink-0 mr-3 flex items-center justify-center">
                  <input
                    type="checkbox"
                    id={`participant-${member.id}`}
                    checked={isSelected}
                    onChange={() => toggleParticipant(member.id)}
                  />
                </div>
              )}              
              {/* 멤버 이름 */}
              <div 
                className="flex-grow cursor-pointer text-gray-800 font-bold h-6"
                onClick={() => toggleParticipant(member.id)}
              >
                {member.user_name} {member.user_id === currentUser?.id ? '(나)' : ''}
              </div>
              
              {/* 분배 금액 또는 입력 UI */}
              <div className="flex-shrink-0 ml-auto">
                {splitType === 'equally' && (
                  <span className="text-gray-600 font-medium font-mono-number">
                      {isSelected && amount > 0 ? formatCurrency(calculateShare(member.id), selectedCurrency) : `${currencies[selectedCurrency].symbol}0`}
                  </span>
                )}
                
                {splitType === 'parts' && (
                  <div className="flex items-center font-mono-number gap-2">
                    {/* 금액 표시 */}
                    <span className="font-medium text-gray-600">
                      {isSelected && amount > 0 ? formatCurrency(calculateShare(member.id), selectedCurrency) : `${currencies[selectedCurrency].symbol}0`}
                    </span>
                    
                    {/* 배수 표시 */}
                    <div className="flex items-center gap-1 font-bold">
                      <button
                        type="button"
                        className="w-6 h-6 rounded-sm bg-gray-50 text-gray-800 flex items-center justify-center text-lg disabled:text-gray-300 disabled:opacity-100"
                        onClick={() => adjustMultiplier(member.id, -1)}
                        disabled={multipliers[member.id] <= 0}
                      >
                        −
                      </button>
                      
                      <span className="font-bold text-gray-800">
                        {multipliers[member.id] || 0}x
                      </span>
                      
                      <button
                        type="button"
                        className="w-6 h-6 rounded-sm bg-gray-50 text-gray-800 flex items-center justify-center text-lg disabled:text-gray-300 disabled:opacity-100"
                        onClick={() => adjustMultiplier(member.id, 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
                
                {splitType === 'amounts' && (
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-32 text-right font-mono-number"
                    style={{padding: '0 0 1px 0', borderRadius: 0, border: 'none', borderBottom: '1px solid #eeeff3'}}
                    value={formattedManualAmounts[member.id] || formatCurrency(manualAmounts[member.id] || 0, selectedCurrency)}
                    onChange={(e) => handleManualAmountInputChange(member.id, e.target.value)}
                    onBlur={() => handleManualAmountBlur(member.id)}
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                    placeholder={`${currencies[selectedCurrency].symbol}0`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ParticipantList; 